import { Kit, KitType, KitVariant } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import useFeature from 'services/features/useFeature';

const FIRST_LOOK_CUTOFF_DATE_DEFAULT = '2026-04-09';

export interface FirstLookFeature {
  enabled: boolean;
  cutoffDate?: Date;
}

export interface FirstLookFeatureFlags {
  cutoffDate?: Date;
}

export const useFirstLookFeature = (featureName: string): FirstLookFeature => {
  const ff = useFeature(featureName);
  const cutoffDate = ff.variables?.cutoff_date;
  return {
    enabled: ff.isFeatureEnabled,
    cutoffDate: cutoffDate ? new Date(cutoffDate) : undefined,
  };
};

export const shouldShowFirstLook = (
  kit: Kit | undefined,
  featureFlags: FirstLookFeatureFlags,
): boolean => {
  if (!kit) return false;

  if (!kit.date_results_ready) return false;
  const resultsReadyDate = new Date(kit.date_results_ready);

  const firstLookVersion = getFirstLookVersion(kit);
  switch (firstLookVersion) {
    case 'not_supported':
      return false;
    default:
      const cutoffDate =
        featureFlags.cutoffDate || new Date(FIRST_LOOK_CUTOFF_DATE_DEFAULT);
      return resultsReadyDate >= cutoffDate;
  }
};

function getFirstLookVersion(kit: Kit) {
  const isPregnant = kit.pregnant === true || kit.pregnant === 1;
  if (isPregnant) return 'not_supported';

  const kitAgeGroup = getAgeGroup(kit.age_at_sampling ?? 0);
  const isAdult = kitAgeGroup === AgeGroup.ADULT;
  if (!isAdult) return 'v2';

  const hasStoolChemistryVariant = kit.variants?.some(
    (variant: KitVariant) => variant.sampling_type === 'stool_chemistry',
  );
  if (hasStoolChemistryVariant) return 'v2';

  if (kit.sampling_type === KitType.VAGINAL) return 'v2_vaginal';

  return 'v1';
}
